/**
 * Classify what the server said about a decide/retry attempt.
 *
 * 🛑 WHY THIS IS A MODULE AND NOT AN `if` IN A COMPONENT. The App Server distinguishes SEVEN
 * outcomes on a failed decide, and each states a DIFFERENT lock state — that distinction is the
 * entire point of the durable apply contract behind it. Collapsing them into "Something went wrong"
 * throws away the only information an operator has about whether a door opened.
 *
 * Two of them are not errors in the ordinary sense at all:
 *
 *   applyPending  we MAY have applied the decision and could not finalise it. The outcome is
 *                 UNKNOWN. Retrying could grant twice.
 *   applyStuck    a previous decision could not be applied. `applied` says whether the outcome is
 *                 unknown or provably did not happen, and `terminal` says whether anything will
 *                 ever reconcile it.
 *
 * Those two must not be shown as a toast that vanishes. `severity: 'ambiguous'` marks them, and the
 * container renders them as a modal the operator has to dismiss. A disappearing notification for
 * "a door may have opened and we cannot tell" is a misreport by affordance, even if the words are
 * right.
 *
 * Returns `{ kind, severity, title, detail, canRetryNow }`.
 *   severity: 'success' | 'refused' | 'conflict' | 'ambiguous' | 'error'
 *   canRetryNow: the SAME action is worth trying again immediately (a lost race), as opposed to
 *                needing a fix, a wait, or a human at the lock.
 */

/** The server's envelope: { statusCode, message, info, data }. */
const infoOf = (res, fallback) => res?.info || res?.message || errText(res?.error) || fallback;

/**
 * An `error` may be an Error, a string, or the failSafeHandler's `{ error: { message, ... } }`.
 * String()-ing the object form yields "[object Object]", which is what the operator would be shown
 * as the whole explanation.
 */
const errText = (e) => {
	if (!e) return '';
	if (typeof e === 'string') return e;
	return e.message || e.info || e.errors || '';
};

/**
 * Did the reply never come back from the network at all?
 *
 * 🛑 SUPERFETCH NEVER REJECTS. src/Library/helpers/superFetch.js catches its own fetch and
 * RETURNS `{ errors: 'Server Error', error }`, so a saga try/catch around it is dead code and a
 * dropped connection arrives here looking like an ordinary body with no statusCode.
 *
 * That distinction is not cosmetic. A decide POST that vanished at the gateway MAY have been
 * applied - the server could have granted access and lost only the reply. Reporting it as a plain
 * error ("Could not decide", in a toast that fades) invites the retry that double-grants, which is
 * the single failure the durable apply contract exists to prevent. So it classifies as AMBIGUOUS,
 * exactly like the server's own applyPending.
 */
const isTransportFailure = (res) => !!res && res.statusCode === undefined && (res.errors != null || res.error != null);

export const OUTCOME = Object.freeze({
	DECIDED: 'decided',
	ADAPTER_REFUSED: 'adapterRefused',
	IN_FLIGHT: 'inFlight',
	EXPIRED: 'expired',
	ALREADY_DECIDED: 'alreadyDecided',
	RETRYABLE: 'retryable',
	APPLY_STUCK: 'applyStuck',
	APPLY_PENDING: 'applyPending',
	FORBIDDEN: 'forbidden',
	TARGET_MOVED: 'targetMoved',
	NOT_FOUND: 'notFound',
	ERROR: 'error'
});

export const classifyDecide = (res, { decision } = {}) => {
	const data = res?.data || {};
	const status = res?.statusCode;

	// FIRST, before any status branch: no status at all + an error payload is a lost reply, and the
	// outcome of a decide we never heard back on is unknown. See isTransportFailure.
	if (isTransportFailure(res))
		return {
			kind: OUTCOME.APPLY_PENDING,
			severity: 'ambiguous',
			title: 'No reply from the server',
			detail: `The decision may or may not have been recorded${
				errText(res.error) ? ` (${errText(res.error)})` : ''
			}. Re-read the queue and verify the target before deciding again - do not simply retry.`,
			canRetryNow: false
		};

	if (status === 200 || status === 201)
		return {
			kind: OUTCOME.DECIDED,
			severity: 'success',
			title: `Request ${decision === 'rejected' ? 'rejected' : 'approved'}`,
			detail: infoOf(res, ''),
			canRetryNow: false
		};

	if (status === 403)
		return {
			kind: OUTCOME.FORBIDDEN,
			severity: 'error',
			title: 'Not your queue',
			detail: infoOf(res, 'You do not have permission to decide requests for this site.'),
			canRetryNow: false
		};

	if (status === 404)
		return {
			kind: OUTCOME.NOT_FOUND,
			severity: 'error',
			title: 'Nothing to decide',
			detail: infoOf(res, 'No pending, unexpired approval request to decide.'),
			canRetryNow: false
		};

	if (status === 409) {
		// 🛑 ORDER MATTERS, and it mirrors routesV5/approvals/index.js. The ambiguous states are
		// tested FIRST: `applyPending` and `applyStuck` both also look like ordinary conflicts, and
		// reporting either as "try again" is precisely the misreport the apply contract prevents.
		if (data.applyPending)
			return {
				kind: OUTCOME.APPLY_PENDING,
				severity: 'ambiguous',
				title: 'Outcome unknown — do not retry',
				// "MAY have been applied": both producers set applied:'unknown', and one is the
				// adapter-throw path where nothing may have happened at all. Saying "was applied"
				// would contradict the very field beside it.
				detail: `${infoOf(
					res,
					'The decision may have been applied but could not be finalised.'
				)} Verify the target before deciding again.`,
				canRetryNow: false
			};

		if (data.applied !== undefined || data.terminal !== undefined)
			return {
				kind: OUTCOME.APPLY_STUCK,
				severity: 'ambiguous',
				title: data.terminal ? 'Needs an operator — retrying will not help' : 'Being reconciled',
				detail: infoOf(res, 'A previous decision could not be applied.'),
				// A terminal row has a retry path (POST /:id/retry) but it is NOT this action, and it
				// requires the operator to assert they checked the target first.
				canRetryNow: false
			};

		if (data.adapter || data.adapterError !== undefined)
			return {
				kind: OUTCOME.ADAPTER_REFUSED,
				severity: 'refused',
				title: 'The underlying action was refused',
				// The adapter's OWN reason when it gave one — "doors 5442 are not at this site" is
				// actionable, "blackout or eligibility" is booking-flavoured boilerplate.
				detail: data.adapter?.reason || infoOf(res, 'The request remains pending.'),
				canRetryNow: false
			};

		if (data.status)
			return {
				kind: OUTCOME.ALREADY_DECIDED,
				severity: 'conflict',
				title: `Already ${data.status}`,
				detail: infoOf(res, `This request was already ${data.status}.`),
				canRetryNow: false
			};

		const detail = infoOf(res, 'The request could not be decided.');

		/**
		 * The last four 409s carry no field of their own beyond `data.code`.
		 *
		 * 🛑 PREFER THE CODE; THE WORDING IS A FALLBACK, NOT THE CONTRACT. Matching prose meant a
		 * copy-edit on the server silently dropped an outcome to the generic conflict below - losing
		 * `canRetryNow`, so an operator who could simply have tried again was told to stop - with
		 * every test on both sides still passing, because each side pins its OWN copy of the string.
		 * The regexes stay only for a server that predates the codes; delete them once prod is past
		 * that, not before.
		 */
		const byCode = {
			IN_FLIGHT: [OUTCOME.IN_FLIGHT, 'Someone else is deciding this', true],
			RETRYABLE: [OUTCOME.RETRYABLE, 'Another decider got there first', true],
			EXPIRED: [OUTCOME.EXPIRED, 'Request expired', false],
			TARGET_MOVED: [OUTCOME.TARGET_MOVED, 'The target moved', false]
		};
		const matched =
			byCode[data.code] ||
			(/currently being applied/i.test(detail)
				? byCode.IN_FLIGHT
				: /released it/i.test(detail)
					? byCode.RETRYABLE
					: /expired/i.test(detail)
						? byCode.EXPIRED
						: /moved site/i.test(detail)
							? byCode.TARGET_MOVED
							: null);
		if (matched) {
			const [kind, title, canRetryNow] = matched;
			// canRetryNow is true only where nothing is known to have happened yet and the block is
			// short-lived (a held claim, a lost race). Never for expiry or a moved target.
			return { kind, severity: 'conflict', title, detail, canRetryNow };
		}

		return { kind: OUTCOME.ERROR, severity: 'conflict', title: 'Could not decide', detail, canRetryNow: false };
	}

	return {
		kind: OUTCOME.ERROR,
		severity: 'error',
		title: 'Could not decide',
		detail: infoOf(res, 'The request could not be decided.'),
		canRetryNow: false
	};
};

/** Retry (`failed_apply → pending`) has its own, smaller vocabulary. */
export const classifyRetry = (res) => {
	const status = res?.statusCode;

	// A lost reply on a retry is NOT ambiguous in the door-opening sense — retry only moves a row
	// from failed_apply back to pending; it applies nothing. But the operator still cannot know
	// whether it landed, so say that rather than "failed".
	if (isTransportFailure(res))
		return {
			kind: OUTCOME.ERROR,
			severity: 'conflict',
			title: 'No reply from the server',
			detail: `The request may or may not have been re-opened${
				errText(res.error) ? ` (${errText(res.error)})` : ''
			}. Re-read the queue before trying again.`,
			canRetryNow: false
		};

	if (status === 200)
		return {
			kind: OUTCOME.DECIDED,
			severity: 'success',
			title: 'Re-opened',
			detail: infoOf(res, 'The request is pending again and can be decided.'),
			canRetryNow: false
		};
	if (status === 403)
		return {
			kind: OUTCOME.FORBIDDEN,
			severity: 'error',
			title: 'Not your queue',
			detail: infoOf(res, ''),
			canRetryNow: false
		};
	if (status === 404)
		return {
			kind: OUTCOME.NOT_FOUND,
			severity: 'error',
			title: 'No such request',
			detail: infoOf(res, ''),
			canRetryNow: false
		};
	return {
		kind: OUTCOME.ERROR,
		severity: 'conflict',
		title: 'Could not re-open',
		detail: infoOf(res, 'The request could not be re-opened.'),
		canRetryNow: false
	};
};

/** Statuses an operator can act on, and how. Drives the row actions. */
export const actionsFor = (status) => ({
	canDecide: status === 'pending',
	canRetry: status === 'failed_apply'
});
